import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { ChevronDown, ChevronUp, Clock, MessageSquare, Mic, Send } from 'lucide-react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { useAddFeedback } from '@/api/campaigns/add-feedback';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { FeedbackProgressModal } from '@/components/feedbacks/feedback-progress-modal';
import { ideasSearchSchema } from '@/utils/search-params';
import { useListCampaigns } from '@/api/campaigns/list-campaigns';
import { useGetInternalCampaign } from '@/api/campaigns/get-internal-campaign';
import { IdeaCard } from '@/components/ideas/idea-card';
import { useOrganization } from '@/contexts/organization-context';

export const Route = createFileRoute('/_authenticated/ideas/')({
  validateSearch: ideasSearchSchema,
  component: IdeasPage
});

interface IdeaSubmission {
  title?: string;
  content: string;
  category?: string;
  formType: 'simple' | 'structured';
}

function IdeasPage() {
  const queryClient = useQueryClient();
  const search = Route.useSearch();
  const { organizationId, projectId: contextProjectId } = useOrganization();
  const projectId = search.projectId || contextProjectId || '';
  const { data: campaign } = useGetInternalCampaign(organizationId, projectId);
  const campaignsQuery = useListCampaigns(organizationId, projectId);
  const campaignId = campaignsQuery.data?.campaigns[0]?.id || null;
  const addFeedback = useAddFeedback(campaignId, projectId);

  // Form states
  const [simpleInput, setSimpleInput] = useState('');
  const [showFullForm, setShowFullForm] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');

  // Sort state
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');

  // Feedback progress modal states
  const [feedbackProgressModalOpen, setFeedbackProgressModalOpen] = useState(false);
  const [currentFeedbackId, setCurrentFeedbackId] = useState<string | null>(null);

  // Fonction de traitement d'idée avec support background
  const processIdea = async (ideaData: IdeaSubmission) => {
    try {
      const feedbackResponse = await addFeedback.mutateAsync({
        title: ideaData.title,
        content: ideaData.content,
        category: ideaData.category
      });
      setCurrentFeedbackId(feedbackResponse.feedbackId);

      // Refresh ideas and open progress modal
      queryClient.invalidateQueries({ queryKey: ['ideas'] });

      setFeedbackProgressModalOpen(true);
    } catch (error) {
      console.error('Error submitting idea:', error);
      toast.error('Erreur', {
        description: "Une erreur est survenue lors de l'enregistrement de l'idée."
      });
      setFeedbackProgressModalOpen(false);
    }
  };

  const handleSubmitIdea = (isSimpleMode: boolean) => {
    setCurrentFeedbackId(null);
    let ideaData: IdeaSubmission;

    if (isSimpleMode) {
      if (!simpleInput.trim()) return;
      ideaData = {
        content: simpleInput.trim(),
        formType: 'simple'
      };
      // Reset form immediately for simple mode
      setSimpleInput('');
    } else {
      if (!description.trim() && !title.trim()) return;
      ideaData = {
        content: description.trim() || title.trim(),
        title: title.trim() || undefined,
        category: category.trim() || undefined,
        formType: 'structured'
      };
      // Reset form immediately for structured mode
      setTitle('');
      setDescription('');
      setCategory('');
    }

    // Open modal and start processing
    // setProcessingModalOpen(true);
    processIdea(ideaData);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Input Section - Centered Vertically */}
      <div className="h-[85vh] flex items-center justify-center px-4 pt-16">
        <div className="container mx-auto max-w-3xl space-y-8">
          {/* Header */}
          <div className="text-center space-y-4 animate-fade-in-up">
            <h1 className="text-4xl md:text-5xl font-bold">
              Capturez vos{' '}
              <span className="bg-linear-to-r from-primary to-secondary bg-clip-text text-transparent">idées</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Enregistrez vos réflexions et idées d&apos;amélioration pour votre projet
            </p>
          </div>

          {/* Simple Input or Full Form */}
          <Card className="border-2 animate-fade-in overflow-hidden" style={{ animationDelay: '0.1s' }}>
            <CardContent className="p-6 space-y-4">
              {!showFullForm ? (
                // Simple AI-style input
                <div className="space-y-4">
                  <div className="relative">
                    <Textarea
                      placeholder="Notez votre idée ou votre réflexion..."
                      rows={3}
                      value={simpleInput}
                      onChange={(e) => setSimpleInput(e.target.value)}
                      className="pr-24 resize-none text-base"
                    />
                    <Button size="icon" variant="outline" className="absolute bottom-3 right-16 h-10 w-10 rounded-full">
                      <Mic className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="hero"
                      className="absolute bottom-3 right-3 h-10 w-10 rounded-full"
                      onClick={() => handleSubmitIdea(true)}
                      disabled={!simpleInput.trim()}
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex items-center justify-between">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowFullForm(true)}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <ChevronDown className="mr-2 h-4 w-4" />
                      Formulaire structuré
                    </Button>
                  </div>
                </div>
              ) : (
                // Full structured form
                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="h-5 w-5 text-primary" />
                      <h3 className="font-semibold">Formulaire structuré</h3>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowFullForm(false)}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <ChevronUp className="mr-2 h-4 w-4" />
                      Mode simple
                    </Button>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Titre</label>
                    <Input
                      placeholder="Une courte description de votre idée..."
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Description détaillée</label>
                    <Textarea
                      placeholder="Expliquez votre idée en détail..."
                      rows={4}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Catégorie (optionnel)</label>
                    <Input
                      placeholder="Fonctionnalité, Design, Performance..."
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      {/* Liste des idées */}
      <div className="pb-20 px-4 bg-muted/30">
        <div className="container mx-auto max-w-6xl space-y-6 pt-8 pb-12">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold">Liste de mes idées</h2>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                className="gap-2"
              >
                <Clock className="h-4 w-4" />
                {sortOrder === 'desc' ? 'Plus récents' : 'Plus anciens'}
              </Button>
            </div>
          </div>

          <div className="space-y-6">
            {(campaign?.ideas || [])
              .sort((a, b) => {
                const timeA = new Date(a.createdOn).getTime();
                const timeB = new Date(b.createdOn).getTime();
                return sortOrder === 'desc' ? timeB - timeA : timeA - timeB;
              })
              .map((idea, index) => (
                <IdeaCard key={idea.id} idea={idea} index={index} showActions={false} from="ideas" />
              ))}
          </div>
        </div>
      </div>

      <FeedbackProgressModal
        open={feedbackProgressModalOpen}
        onOpenChange={(o) => setFeedbackProgressModalOpen(o)}
        organizationId={organizationId}
        projectId={projectId}
        feedbackId={currentFeedbackId}
      />
    </div>
  );
}
